import ScheduledMeetingActions from "./ScheduledMeetingActions";
import ScheduledEventTitle from "./ScheduledEventTitle";
import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import CandidateScheduleDialog, { InterviewSearchField } from "./CandidateScheduleDialog";
import { SCHEDULE_EVENT, INTERVIEWS_CHANGED, interviewsChanged, loadInterviews, schedulingRequest } from "../utils/interviewScheduling";

export default function InterviewSchedulingHub() {
  const [context, setContext] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [candidateText, setCandidateText] = useState("");
  const [jobText, setJobText] = useState("");
  const [editor, setEditor] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const open = event => { setContext(event.detail || {}); setRecords([]); setError(""); setEditor(null); setCandidateText(event.detail?.candidate?.full_name || ""); setJobText(event.detail?.job?.job_title || ""); };
    window.addEventListener(SCHEDULE_EVENT, open);
    return () => window.removeEventListener(SCHEDULE_EVENT, open);
  }, []);
  const candidateId = context?.candidateId || null, jobId = context?.jobId || null, contactId = context?.contactId || null;
  const isOpen = context !== null;
  const discovery = context?.kind === "discovery";
  const leadId = context?.leadId;
  const visibleRecords = records.filter(record => record.interview_status !== "cancelled");
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    setLoading(true); setError("");
    if (discovery) {
      const load = () => schedulingRequest(`/calendar/discovery-calls?leadId=${encodeURIComponent(leadId)}`)
        .then(data => { if (active) { setRecords(data.calls || []); setError(""); } })
        .catch(err => { if (active) setError(err.message); })
        .finally(() => { if (active) setLoading(false); });
      load(); window.addEventListener(INTERVIEWS_CHANGED, load);
      return () => { active = false; window.removeEventListener(INTERVIEWS_CHANGED, load); };
    }
    const query = new URLSearchParams();
    if (candidateId) query.set("candidateId", candidateId);
    if (jobId) query.set("jobId", jobId);
    schedulingRequest(`/calendar/scheduling-context?${query}`).then(data => {
      if (!active) return;
      setContext(current => ({ ...current, ...data, jobId: data.job?.id || current.jobId, interviewers: data.interviewers?.length ? data.interviewers : current.contact ? [current.contact] : [] }));
      if (data.candidate) setCandidateText(data.candidate.full_name);
      if (data.job) setJobText(data.job.job_title);
    }).catch(err => { if (active) setError(err.message); }).finally(() => { if (active) setLoading(false); });
    const load = () => loadInterviews({ candidateId, jobId, contactId, includeDiscovery: !candidateId && !jobId }).then(data => { if (active) setRecords(data.interviews || []); }).catch(err => { if (active) setError(err.message); });
    load(); window.addEventListener(INTERVIEWS_CHANGED, load);
    return () => { active = false; window.removeEventListener(INTERVIEWS_CHANGED, load); };
  }, [isOpen, candidateId, jobId, contactId, discovery, leadId]);

  async function changeInterview(record, action) {
    setBusy(true); setError("");
    try { await schedulingRequest(`/calendar/${discovery ? "discovery-calls" : "interviews"}/${record.id}`, { method: "PATCH", body: JSON.stringify({ action }) }); interviewsChanged(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  return <>
    <Dialog open={isOpen && !editor} onClose={busy ? undefined : () => setContext(null)} maxWidth="md" fullWidth>
      <DialogTitle data-popup-header>{discovery ? "Discovery Calls" : "Interviews"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          {!discovery && <><InterviewSearchField required={false} label="Candidate" endpoint="candidates" value={candidateText} disabled={busy || loading}
            onChange={value => { setCandidateText(value); setContext(current => ({ ...current, candidate: null, candidateId: null })); }}
            onSelect={person => { if (person) setContext(current => ({ ...current, candidateId: person.id, candidate: { id: person.id, full_name: person.label, email: person.email } })); }} />
          <InterviewSearchField label="Job" endpoint="jobs" value={jobText} required={false} requireCompany={false} disabled={busy || loading}
            onChange={value => { setJobText(value); setContext(current => ({ ...current, jobId: null, job: null })); }}
            onSelect={job => { if (job) setContext(current => ({ ...current, jobId: job.id, job: { id: job.id, job_title: job.label, company_id: job.company_id, company_name: job.company_name } })); }} />
          <Box><Button variant="contained" startIcon={<EventOutlinedIcon />} disabled={busy || loading}
            onClick={() => setEditor({ candidate: context.candidate, context })}>Schedule interview</Button></Box></>}
          {loading && <CircularProgress size={24} />}
          {!loading && !visibleRecords.length && <Typography color="text.secondary">No scheduled meetings.</Typography>}
          {visibleRecords.map(record => <Box key={record.id} sx={{ py: 1, minWidth: 0, ...(Number(context?.interviewId) === record.id ? { borderLeft: "3px solid #3451F4", pl: 2 } : {}) }}>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap"><Typography fontWeight={600} sx={{ overflowWrap: "anywhere" }}><ScheduledEventTitle record={record} title={record.event_title || [record.candidate_name, record.job_title].filter(Boolean).join(" - ")} onOpenCandidate={() => setContext(null)} /></Typography><Chip size="small" label={record.interview_status || "scheduled"} /></Stack>
            <Typography sx={{ mt: 1 }}>{new Date(record.starts_at).toLocaleString()} - {new Date(record.ends_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit", timeZoneName: "short" })}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>{(record.interviewers || []).map(person => `${person.name || "Interviewer"} (${person.email})`).join(", ") || record.contact_name}</Typography>
            {record.location && <Typography variant="body2">{record.location}</Typography>}
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ my: 1 }}>
              {record.calendar_url && <Button href={record.calendar_url} target="_blank" rel="noopener noreferrer">View in Calendar</Button>}
              {record.google_meet_link && record.interview_status !== "cancelled" && <Button href={record.google_meet_link} target="_blank" rel="noopener noreferrer">Join meeting</Button>}
              {record.can_manage && record.interview_status !== "cancelled" && <>
                <ScheduledMeetingActions record={record} />
                <Button disabled={busy || record.interview_status === "confirmed"} onClick={() => changeInterview(record, "confirm")}>Mark confirmed</Button>
              </>}
            </Stack><Divider />
          </Box>)}
        </Stack>
      </DialogContent>
      <DialogActions data-popup-footer><Button disabled={busy} onClick={() => setContext(null)}>Close</Button></DialogActions>
    </Dialog>
    {editor && <CandidateScheduleDialog open {...editor} onClose={() => setEditor(null)} onSaved={() => interviewsChanged()} />}

  </>;
}
